import styled from 'styled-components';
import { Input, Loader, type InputProps } from 'semantic-ui-react';
import { MagnifyingGlass, X } from '@phosphor-icons/react';
import { forwardRef, useState, type ChangeEvent } from 'react';
import { theme } from '../utility';

const SearchIcon = styled(MagnifyingGlass)`
  pointer-events: none;
  cursor: default;
  position: absolute;
  line-height: 1;
  text-align: center;
  top: 8px;
  right: 7px;
  margin: 0;
  opacity: 0.5;
  border-radius: 0 0.28571429rem 0.28571429rem 0;
  transition: opacity 0.3s ease;
`;

const CrossIcon = styled(X)`
  cursor: pointer;
  position: absolute;
  line-height: 1;
  text-align: center;
  pointer-events: all;
  top: 8px;
  right: 7px;
  margin: 0;
  opacity: 0.5;
  border-radius: 0 0.28571429rem 0.28571429rem 0;
  transition: opacity 0.3s ease;
`;

interface IStyledInputProps {
  showSecondary?: boolean | string;
}

const StyledInput = styled(Input)<IStyledInputProps>`
  .secondary-icon {
    display: none;
  }

  &:not(.small, .tiny, .mini, .large) {
    height: 40px;
  }

  input {
    border-radius: 5px !important;
    border: 1px solid #acadb6 !important;
  }

  &.no-border {
    input {
      border: none !important;
      box-shadow: 0 2px 15px rgba(57, 57, 72, 0.05);
    }
  }

  .default-icon {
    display: ${({ showSecondary }) => (showSecondary ? 'none' : 'block')} !important;
  }

  .secondary-icon {
    display: ${({ showSecondary }) => (!showSecondary ? 'none' : 'block')} !important;
  }
`;

interface IThemeProps {
  theme?: {
    colors: {
      primaryLight: string;
      white: string;
      primary: string;
      semanticBorder: string;
    };
  };
}

const StyledTitleInput = styled(Input)<IStyledInputProps & IThemeProps>`
  .secondary-icon {
    display: none;
  }

  input {
    background: ${({ theme }) => theme?.colors?.primaryLight || '#000'} !important;
    border-radius: 5px !important;
    color: ${({ theme }) => theme?.colors?.white || '#fff'} !important;
  }

  &.no-border {
    input {
      border: none !important;
      box-shadow: 0 2px 15px rgba(57, 57, 72, 0.05);
    }
  }

  .default-icon {
    display: ${({ showSecondary }) => (showSecondary ? 'none' : 'block')} !important;
  }

  .secondary-icon {
    display: ${({ showSecondary }) => (!showSecondary ? 'none' : 'block')} !important;
  }
`;

const LoaderCircle = styled(Loader)`
  position: absolute;
  pointer-events: none;
  cursor: default;
  line-height: 1;
  text-align: center;
  top: 20px !important;
  right: -4px !important;
  left: unset !important;
  bottom: unset !important;
`;

interface ISearchInputProps extends Omit<InputProps, 'onChange' | 'icon' | 'value'> {
  placeholder?: string;
  search?: string;
  onChange?: (value: string) => void;
  setSearch?: (value: string) => void;
  loading?: boolean;
  iconSize?: number;
  value?: string;
}

export const SearchInput = forwardRef<Input, ISearchInputProps>(
  (
    { placeholder, search, onChange, setSearch, loading, iconSize = 24, ...props },
    ref,
  ) => {
    const onChangeHandler = (value: string): void => {
      setSearch && setSearch(value);
      onChange && onChange(value);
    };

    return (
      <StyledInput
        {...props}
        ref={ref}
        showSecondary={props?.value !== undefined ? props.value : search}
        placeholder={placeholder || ' '}
        value={search}
        onChange={(_, { value }) => {
          onChangeHandler(value as string);
        }}
        icon={
          loading ? (
            <LoaderCircle size="small" active />
          ) : (
            <>
              <SearchIcon className="default-icon" size={iconSize} />
              <CrossIcon
                onMouseDown={() => onChangeHandler('')}
                className="secondary-icon"
                size={iconSize}
              />
            </>
          )
        }
      />
    );
  },
);

SearchInput.displayName = 'SearchInput';

const HideableStyledInput = styled.div<IThemeProps>`
  display: flex;
  border: 1px solid ${({ theme }) => theme?.colors?.semanticBorder || '#ccc'};
  border-radius: 5px;
  height: 30.5px;
  margin-left: 10px;
  align-items: center;
  padding: 7px;

  .icon {
    min-width: 15px;
  }

  transition: width 0.2s linear;
  width: 30.5px;
  justify-content: end;

  &.open {
    width: 250px;
  }

  &.close {
    width: 30.5px;
    cursor: pointer;

    &:hover {
      color: white;
      background: ${({ theme }) => theme?.colors?.primary || '#000'};
    }
  }
`;

const InputHidden = styled.input<IThemeProps>`
  outline: none;
  border: none;
  flex: 1;
  font-size: 12px;

  &.open {
    animation: showInput 0.2s linear forwards;
  }
  &.close {
    animation: hideInput 0.2s linear forwards;
  }

  @keyframes showInput {
    0% {
      display: none;
      opacity: 0;
    }
    90% {
      display: none;
      opacity: 0;
    }
    100% {
      opacity: 1;
      display: block;
    }
  }

  @keyframes hideInput {
    0% {
      opacity: 1;
      display: block;
      background-color: ${({ theme }) => theme?.colors?.primary || '#000'};
    }
    1% {
      opacity: 0;
      display: none;
    }
    100% {
      opacity: 0;
      display: none;
    }
  }
`;

interface IHideSearchInputProps {
  placeholder?: string;
  search?: string;
  onChange?: (value: string) => void;
  setSearch: (value: string) => void;
  loading?: boolean;
}

export function HideSearchInput({
  placeholder,
  search,
  onChange,
  setSearch,
  loading,
}: IHideSearchInputProps): JSX.Element {
  const [show, setShow] = useState<boolean>(false);

  const onChangeHandler = (value: string): void => {
    setSearch && setSearch(value);
    onChange && onChange(value);
  };

  const closeSearchBar = (): void => {
    setShow(false);
    setSearch('');
  };

  return (
    <HideableStyledInput
      onClick={() => {
        if (!show) {
          setShow(true);
        }
      }}
      className={show ? 'open' : 'close'}
    >
      <InputHidden
        value={search}
        onChange={(e: ChangeEvent<HTMLInputElement>) => onChangeHandler(e.target.value)}
        placeholder={placeholder}
        className={`input ${show ? 'open' : 'close'}`}
      />
      {loading ? (
        <Loader active inline size="mini" className="icon" />
      ) : show ? (
        <div className="pt-1 cursor-pointer" onClick={closeSearchBar}>
          <X size={15} className="icon" />
        </div>
      ) : (
        <MagnifyingGlass size={15} className="icon" />
      )}
    </HideableStyledInput>
  );
}

export const TitleSearchInput = forwardRef<Input, ISearchInputProps>(
  (
    { placeholder, search, onChange, setSearch, loading, iconSize = 14, ...props },
    ref,
  ) => {
    const onChangeHandler = (value: string): void => {
      setSearch && setSearch(value);
      onChange && onChange(value);
    };

    return (
      <StyledTitleInput
        {...props}
        ref={ref}
        showSecondary={props?.value !== undefined ? props.value : search}
        placeholder={placeholder || ' '}
        value={search}
        size="mini"
        onChange={(_, { value }) => {
          onChangeHandler(value as string);
        }}
        icon={
          loading ? (
            <LoaderCircle size="small" active />
          ) : (
            <>
              <SearchIcon
                color={theme.colors.white}
                className="default-icon"
                size={iconSize}
              />
              <CrossIcon
                color={theme.colors.white}
                onMouseDown={() => onChangeHandler('')}
                className="secondary-icon"
                size={iconSize}
              />
            </>
          )
        }
      />
    );
  },
);

TitleSearchInput.displayName = 'TitleSearchInput';
